import React from 'react';
import PropTypes from 'prop-types';

import InfoTip from './InfoTip';
import { ACCOUNT_COLUMN, buildBands } from './columns';
import { BANDS } from './copy';
import { BandCell, ColumnCell, ColumnLabel, ControlColumnCell } from './styles';

/**
 * The two-tier header: PERMISSIONS and DISCOVERABILITY spanning their columns,
 * with the per-column headings beneath. Bands are derived from the columns, so a
 * role added server-side widens PERMISSIONS without this file changing.
 *
 * ACCOUNT sits in the second row alongside the other column headings rather than
 * spanning both, so it takes the same fill they do. Spanning both would be
 * tidier markup — one header cell per column — but a cell cannot fill only half
 * its height, and the design leaves the corner above ACCOUNT clear.
 */
function MatrixHead({ columns }) {
  const [, ...bands] = buildBands(columns);
  const [accountColumn, ...rest] = columns;

  // Only the DISCOVERABILITY boundary is drawn. PERMISSIONS begins where the
  // account column ends, and the design leaves that edge open.
  const dividerKey = rest.find((column) => column.band === 'discoverability')
    ?.key;

  return (
    <thead>
      <tr>
        {/* The corner above ACCOUNT. A BandCell so the fill and the box it
            resets live in one place, `as="td"` because there is no heading
            here and an empty th is announced as one. */}
        <BandCell as="td" />

        {bands.map((band) => (
          <BandCell key={band.band} colSpan={band.span} scope="colgroup">
            {BANDS[band.band]}
          </BandCell>
        ))}
      </tr>

      <tr>
        <ColumnCell scope="col">{accountColumn.title}</ColumnCell>

        {rest.map((column) => (
          <ControlColumnCell
            key={column.key}
            $bandStart={column.key === dividerKey}
            scope="col"
          >
            <ColumnLabel>
              {column.title}
              {column.info && (
                <InfoTip label={`About ${column.title}`}>{column.info}</InfoTip>
              )}
            </ColumnLabel>
          </ControlColumnCell>
        ))}
      </tr>
    </thead>
  );
}

MatrixHead.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      band: PropTypes.string,
      info: PropTypes.string,
      key: PropTypes.string,
      title: PropTypes.string,
    }),
  ).isRequired,
};

export { ACCOUNT_COLUMN };
export default MatrixHead;
